import { Card, Modal, ModalProps } from 'antd';
import { SymptomCard2 } from 'features/patient/_components/symptom-card/symptom-card-2';
import { prepareBrainFunctionVisualisation } from 'lib/utils/business/brainfunction-categories';
import {
  BrainFunctionVisualisation,
  DynamicBrainFunctionVisualisation,
  RequiredVisualisationData
} from 'lib/utils/business/brainfunction-categories/types';
import { useCallback, useState } from 'react';
import { DialBreakdown } from '../display-supplemental';
import { Text } from 'components/mvp-typography';
import { ErrorBoundary } from 'react-error-boundary';
import { ErrorDisplay } from 'components/ErrorDisplay';

export interface BrainFunctionVisualisationComponentProps {
  visualisation: BrainFunctionVisualisation;
  visualisationData: RequiredVisualisationData;
}

/**
 * Component to display a BrainFunction visualisation
 *
 * Provides a simple API for visualising brainfunction visualisations with less boilerplate
 */
export function BrainFunctionVisualisationComponent(
  props: BrainFunctionVisualisationComponentProps
) {
  return (
    <ErrorBoundary
      fallbackRender={props => (
        <Card>
          <ErrorDisplay error={props.error} />
        </Card>
      )}
    >
      <BrainFunctionVisualisationComponentNoErrorBoundary {...props} />
    </ErrorBoundary>
  );
}

/**
 * Component to display a BrainFunction visualisation
 *
 * Provides a simple API for visualising brainfunction visualisations with less boilerplate
 */
function BrainFunctionVisualisationComponentNoErrorBoundary(
  props: BrainFunctionVisualisationComponentProps
) {
  const brainFunction = prepareBrainFunctionVisualisation(
    props.visualisation,
    props.visualisationData
  );
  const data = brainFunction.brainFunction.getValues(props.visualisationData);
  const modalState = useModalState();

  return (
    <>
      <SymptomCard2
        label={brainFunction?.brainFunction.label}
        data={data}
        brainFunction={brainFunction.brainFunction}
        disabled={
          // If the data for the symptom is empty (falsy)
          data.length === 0
        }
        showMoreInfo={props.visualisation.type === 'dynamic'}
        onMoreInfoClick={modalState.open}
      />
      {props.visualisation.type === 'dynamic' && (
        <DynamicBrainfunctionBreakdownModal
          width={'fit-content'}
          open={modalState.isShowing}
          onCancel={() => modalState.close()}
          onClose={() => modalState.close()}
          onOk={() => modalState.close()}
          visualisation={props.visualisation}
          patientInfo={props.visualisationData}
          cancelButtonProps={{ disabled: true, hidden: true }}
        />
      )}
    </>
  );
}

/**
 * A custom hook for managing modal states
 */
function useModalState() {
  const [isShowing, setIsShowing] = useState(false);
  const open = useCallback(() => setIsShowing(true), []);
  const close = useCallback(() => setIsShowing(false), []);
  return { isShowing, open, close, setIsShowing };
}

function DynamicBrainfunctionBreakdownModal(
  props: Omit<ModalProps, ''> & {
    visualisation: DynamicBrainFunctionVisualisation;
    patientInfo: RequiredVisualisationData;
  }
) {
  const { visualisation, patientInfo, ...rest } = props;
  return (
    <Modal {...rest}>
      <div
        style={{
          paddingTop: '1rem'
        }}
      >
        <Text>
          The {props.visualisation.label} Brainfunction consists of the
          following Brainfunctions:
        </Text>
        <DialBreakdown
          patientInfo={patientInfo}
          composedVisualisation={visualisation}
          sourceVisualisations={visualisation.composition}
        />
      </div>
    </Modal>
  );
}
